import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Download, Plus, AlertTriangle, ShieldCheck, ArrowLeft, Trash2, Pencil, X, NotebookPen, MessageSquare, Calendar as CalIcon, Timer, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const RISK_TINT = {
  low: "bg-secondary/15 text-secondary border-secondary/30",
  medium: "bg-accent/30 text-[#7A5A1E] border-accent/50",
  high: "bg-primary/15 text-primary border-primary/40",
};
const KIND_LABEL = { session: "Session", interaction: "Interaction" };
const INTERACTION_LABELS = { phone_call: "Phone call", no_show: "No-show", parent_contact: "Parent contact", walk_in: "Walk-in", email: "Email", other: "Other" };

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-ZA", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const EMPTY_FORM = {
  kind: "session",
  interaction_type: "phone_call",
  occurred_at: "",
  booking_id: "",
  data: "",
  assessment: "",
  plan: "",
  mood_rating: "",
  risk_level: "",
  attended: "",
  client_summary: "",
};

function NoteForm({ initial, clientId, bookings, onSaved, onCancel }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [newTplName, setNewTplName] = useState("");
  const isEdit = !!initial?.note_id;

  useEffect(() => {
    api.get("/counsellor/note-templates").then((r) => setTemplates(r.data)).catch(() => { /* silent */ });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (tplId) => {
    setSelectedTpl(tplId);
    if (!tplId) return;
    const t = templates.find((x) => x.template_id === tplId);
    if (!t) return;
    setForm((f) => ({
      ...f,
      data: (f.data ? f.data + "\n\n" : "") + (t.data || ""),
      assessment: (f.assessment ? f.assessment + "\n\n" : "") + (t.assessment || ""),
      plan: (f.plan ? f.plan + "\n\n" : "") + (t.plan || ""),
    }));
    toast.success(`Inserted template: ${t.name}`);
  };

  const saveAsTemplate = async () => {
    if (!newTplName.trim()) { toast.error("Give the template a name"); return; }
    try {
      const { data } = await api.post("/counsellor/note-templates", {
        name: newTplName.trim(),
        data: form.data || "",
        assessment: form.assessment || "",
        plan: form.plan || "",
      });
      setTemplates((prev) => [...prev, data]);
      setShowSaveTpl(false);
      setNewTplName("");
      toast.success(`Saved template "${data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    }
  };

  const deleteTemplate = async () => {
    const t = templates.find((x) => x.template_id === selectedTpl);
    if (!t || !t.counsellor_id) return;
    if (!window.confirm(`Delete your template "${t.name}"?`)) return;
    try {
      await api.delete(`/counsellor/note-templates/${t.template_id}`);
      setTemplates((prev) => prev.filter((x) => x.template_id !== t.template_id));
      setSelectedTpl("");
      toast.success("Template deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.data?.trim() && !form.assessment?.trim() && !form.plan?.trim() && !form.client_summary?.trim()) {
      toast.error("Add at least one of: Data, Assessment, Plan, or Client summary.");
      return;
    }
    setSaving(true);
    const payload = {
      client_id: clientId,
      kind: form.kind,
      interaction_type: form.kind === "interaction" ? form.interaction_type : null,
      occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : null,
      booking_id: form.kind === "session" && form.booking_id ? form.booking_id : null,
      data: form.data || "",
      assessment: form.assessment || "",
      plan: form.plan || "",
      mood_rating: form.mood_rating === "" ? null : Number(form.mood_rating),
      risk_level: form.risk_level || null,
      attended: form.kind === "session" && form.attended !== "" ? form.attended === "yes" : null,
      client_summary: form.client_summary || "",
    };
    try {
      if (isEdit) {
        const { client_id, kind, booking_id, ...patch } = payload;
        await api.patch(`/notes/${initial.note_id}`, patch);
        toast.success("Note updated");
      } else {
        await api.post("/notes", payload);
        toast.success("Note saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save note");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none text-sm";
  const labelCls = "text-xs uppercase font-extrabold tracking-[0.18em] text-[#7A7D96]";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="note-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className={labelCls}>Type</div>
          <select className={inputCls} value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="note-kind">
            <option value="session">Session note</option>
            <option value="interaction">Interaction</option>
          </select>
        </div>
        <div>
          <div className={labelCls}>When</div>
          <input type="datetime-local" className={inputCls} value={form.occurred_at} onChange={(e) => set("occurred_at", e.target.value)} data-testid="note-when" />
        </div>
      </div>

      {form.kind === "interaction" && (
        <div>
          <div className={labelCls}>Interaction type</div>
          <select className={inputCls} value={form.interaction_type} onChange={(e) => set("interaction_type", e.target.value)} data-testid="note-interaction-type">
            {Object.entries(INTERACTION_LABELS).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
      )}

      {form.kind === "session" && bookings?.length > 0 && (
        <div>
          <div className={labelCls}>Link to session (optional)</div>
          <select className={inputCls} value={form.booking_id} onChange={(e) => set("booking_id", e.target.value)} data-testid="note-booking">
            <option value="">— none —</option>
            {bookings.map((b) => (
              <option key={b.booking_id} value={b.booking_id}>
                {fmtWhen(b.scheduled_at)} · {b.session_mode === "in_person" ? "in-person" : "online"}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className={labelCls}>Mood (1–10)</div>
          <input type="number" min={1} max={10} className={inputCls} value={form.mood_rating} onChange={(e) => set("mood_rating", e.target.value)} data-testid="note-mood" />
        </div>
        <div>
          <div className={labelCls}>Risk level</div>
          <select className={inputCls} value={form.risk_level} onChange={(e) => set("risk_level", e.target.value)} data-testid="note-risk">
            <option value="">—</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        {form.kind === "session" && (
          <div>
            <div className={labelCls}>Attended</div>
            <select className={inputCls} value={form.attended} onChange={(e) => set("attended", e.target.value)} data-testid="note-attended">
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        )}
      </div>

      <div className="p-3 rounded-2xl border-2 border-dashed border-secondary/40 bg-secondary/5 flex flex-wrap items-center gap-2" data-testid="note-templates-row">
        <div className="text-xs font-extrabold uppercase tracking-wider text-secondary shrink-0">Templates</div>
        <select value={selectedTpl} onChange={(e) => applyTemplate(e.target.value)} className={inputCls + " max-w-xs"} data-testid="note-template-select">
          <option value="">— Load a template —</option>
          <optgroup label="Built-in DAP templates">
            {templates.filter((t) => !t.counsellor_id).map((t) => (
              <option key={t.template_id} value={t.template_id}>{t.name}</option>
            ))}
          </optgroup>
          {templates.filter((t) => t.counsellor_id).length > 0 && (
            <optgroup label="Your saved templates">
              {templates.filter((t) => t.counsellor_id).map((t) => (
                <option key={t.template_id} value={t.template_id}>{t.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        {selectedTpl && templates.find((x) => x.template_id === selectedTpl)?.counsellor_id && (
          <button type="button" onClick={deleteTemplate} className="text-xs text-destructive font-bold hover:underline" data-testid="note-template-delete">Delete this template</button>
        )}
        <button type="button" onClick={() => setShowSaveTpl((v) => !v)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-secondary/40 text-secondary text-xs font-bold hover:bg-secondary/10" data-testid="note-template-save-toggle">
          {showSaveTpl ? "Cancel" : "Save current as template…"}
        </button>
        {showSaveTpl && (
          <div className="w-full flex gap-2 mt-2" data-testid="note-template-save-row">
            <input value={newTplName} onChange={(e) => setNewTplName(e.target.value)} placeholder="Template name (e.g. My CBT intake)" className={inputCls + " flex-1"} data-testid="note-template-name" />
            <button type="button" onClick={saveAsTemplate} className="px-4 py-2 rounded-full bg-secondary text-white text-xs font-bold" data-testid="note-template-save-btn">Save</button>
          </div>
        )}
      </div>

      <div>
        <div className={labelCls}>D — Data / what was observed</div>
        <textarea rows={4} className={inputCls} value={form.data} onChange={(e) => set("data", e.target.value)} placeholder="Presenting concerns, observations, dialogue notes…" data-testid="note-data" />
      </div>
      <div>
        <div className={labelCls}>A — Assessment</div>
        <textarea rows={3} className={inputCls} value={form.assessment} onChange={(e) => set("assessment", e.target.value)} placeholder="Clinical interpretation, hypotheses, themes…" data-testid="note-assessment" />
      </div>
      <div>
        <div className={labelCls}>P — Plan</div>
        <textarea rows={3} className={inputCls} value={form.plan} onChange={(e) => set("plan", e.target.value)} placeholder="Homework, next session focus, referrals…" data-testid="note-plan" />
      </div>
      <div>
        <div className={labelCls}>Client-visible summary <span className="text-primary normal-case font-bold tracking-normal">(this is the ONLY part the client sees)</span></div>
        <textarea rows={3} className={inputCls} value={form.client_summary} onChange={(e) => set("client_summary", e.target.value)} placeholder="A short, supportive recap the client will see on their dashboard." data-testid="note-summary" />
      </div>

      <div className="flex gap-2 justify-end pt-2">
        {onCancel && <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-full border border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5">Cancel</button>}
        <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50" data-testid="note-save-btn">
          {saving ? "Saving…" : isEdit ? "Update note" : "Save note"}
        </button>
      </div>
    </form>
  );
}

function NoteCard({ note, counsellorName, canEdit, onEdit, onDelete }) {
  const riskCls = RISK_TINT[note.risk_level] || "bg-muted/40 text-[#7A7D96] border-[#3D405B]/10";
  return (
    <article className="brand-card p-6" data-testid={`note-card-${note.note_id}`}>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline">{KIND_LABEL[note.kind] || "Note"}{note.interaction_type ? ` · ${INTERACTION_LABELS[note.interaction_type]}` : ""}</div>
          <div className="text-[#3D405B] font-extrabold text-lg mt-1">{fmtWhen(note.occurred_at || note.created_at)}</div>
          <div className="text-xs text-[#7A7D96] mt-1">By {counsellorName || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {note.risk_level && (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-extrabold border ${riskCls}`}>
              <AlertTriangle size={11} /> {note.risk_level}
            </span>
          )}
          {note.mood_rating != null && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-extrabold bg-muted/40 text-[#3D405B]">Mood {note.mood_rating}/10</span>
          )}
          {note.attended != null && note.kind === "session" && (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-extrabold ${note.attended ? "bg-secondary/15 text-secondary" : "bg-primary/15 text-primary"}`}>
              {note.attended ? "Attended" : "No-show"}
            </span>
          )}
        </div>
      </header>
      <div className="space-y-3">
        {note.data && (
          <div><div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[#7A7D96] mb-1">D — Data</div><p className="text-sm text-[#3D405B] whitespace-pre-wrap leading-relaxed">{note.data}</p></div>
        )}
        {note.assessment && (
          <div><div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[#7A7D96] mb-1">A — Assessment</div><p className="text-sm text-[#3D405B] whitespace-pre-wrap leading-relaxed">{note.assessment}</p></div>
        )}
        {note.plan && (
          <div><div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[#7A7D96] mb-1">P — Plan</div><p className="text-sm text-[#3D405B] whitespace-pre-wrap leading-relaxed">{note.plan}</p></div>
        )}
        {note.client_summary && (
          <div className="mt-3 p-3 rounded-2xl bg-accent/15 border border-accent/40">
            <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-primary mb-1 flex items-center gap-1"><ShieldCheck size={11} /> Visible to client</div>
            <p className="text-sm text-[#3D405B] whitespace-pre-wrap leading-relaxed">{note.client_summary}</p>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-[#3D405B]/5">
          <button onClick={onEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-[#3D405B] hover:bg-[#3D405B]/5" data-testid={`note-edit-${note.note_id}`}><Pencil size={12} /> Edit</button>
          <button onClick={onDelete} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-primary hover:bg-primary/10" data-testid={`note-delete-${note.note_id}`}><Trash2 size={12} /> Delete</button>
        </div>
      )}
    </article>
  );
}

export default function CaseFile() {
  const { user } = useAuth();
  const { clientId } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [counsellorMap, setCounsellorMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const isPrivileged = user?.role === "counsellor" || user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [clientsRes, notesRes, bookingsRes] = await Promise.all([
        api.get("/notes/clients").catch(() => ({ data: [] })),
        api.get(`/notes?client_id=${clientId}`),
        api.get("/bookings").catch(() => ({ data: [] })),
      ]);
      const found = (clientsRes.data || []).find((c) => c.user_id === clientId);
      setClient(found || { user_id: clientId, name: "Client", email: "" });
      setNotes(notesRes.data || []);
      const clientBookings = (bookingsRes.data || []).filter((b) => (b.client?.user_id || b.client_id) === clientId);
      setBookings(clientBookings);

      const counsellorIds = Array.from(new Set((notesRes.data || []).map((n) => n.counsellor_id)));
      if (counsellorIds.length) {
        try {
          const { data } = await api.get("/counsellors");
          const map = {};
          for (const c of data) if (counsellorIds.includes(c.user_id)) map[c.user_id] = c.name;
          if (!map[user.user_id]) map[user.user_id] = user.name;
          setCounsellorMap(map);
        } catch (e) { logger.warn("case-file counsellor map load failed:", e?.message); }
      }
    } catch (e) {
      toast.error("Could not load case file");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (clientId && isPrivileged) load(); }, [clientId, isPrivileged]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/notes/export/pdf?client_id=${clientId}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `case_file_${(client?.name || "client").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Case file exported");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const removeNote = async (n) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try { await api.delete(`/notes/${n.note_id}`); toast.success("Note deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const headerRisk = useMemo(() => {
    const highCount = notes.filter((n) => n.risk_level === "high").length;
    return highCount > 0 ? `${highCount} high-risk entr${highCount === 1 ? "y" : "ies"}` : null;
  }, [notes]);

  // Mini-dashboard stats. Split bookings into upcoming (soonest first) + past (most recent first).
  const nowIso = new Date().toISOString();
  const upcomingSessions = useMemo(
    () => bookings
      .filter((b) => b.status === "scheduled" && (b.scheduled_at || "") >= nowIso)
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    [bookings, nowIso],
  );
  const pastSessions = useMemo(
    () => bookings
      .filter((b) => !((b.status === "scheduled" && (b.scheduled_at || "") >= nowIso)))
      .sort((a, b) => (b.scheduled_at || "").localeCompare(a.scheduled_at || "")),
    [bookings, nowIso],
  );
  const completedSessions = useMemo(() => bookings.filter((b) => b.status === "completed"), [bookings]);
  const totalMinutes = useMemo(() => completedSessions.reduce((sum, b) => sum + (b.duration_minutes || 50), 0), [completedSessions]);
  const firstSeen = useMemo(() => {
    if (bookings.length === 0) return null;
    return bookings.reduce((min, b) => (b.scheduled_at && (!min || b.scheduled_at < min) ? b.scheduled_at : min), null);
  }, [bookings]);

  if (!isPrivileged) {
    return <div className="min-h-screen"><Navbar /><main className="max-w-2xl mx-auto px-6 py-12 text-center"><h1 className="text-2xl font-extrabold text-[#3D405B]">Insufficient permissions</h1><p className="text-[#7A7D96] mt-2">Only counsellors and admins can view case files.</p><Link to="/dashboard" className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary text-white font-bold"><ArrowLeft size={14} /> Back</Link></main></div>;
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#7A7D96] hover:text-[#3D405B] mb-4" data-testid="case-back-btn"><ArrowLeft size={14} /> Back</button>

        <header className="brand-card p-7 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="overline mb-2 flex items-center gap-2"><NotebookPen size={13} /> Client file</div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3D405B] tracking-tight truncate">{client?.name || "Client"}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#5F627D]">
                {client?.email && (
                  <a href={`mailto:${client.email}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="case-client-email"><Mail size={12} /> {client.email}</a>
                )}
                {client?.phone && (
                  <a href={`tel:${client.phone}`} className="inline-flex items-center gap-1.5 hover:text-primary" data-testid="case-client-phone"><Phone size={12} /> {client.phone}</a>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.16em] font-extrabold bg-muted/40 text-[#3D405B]">{notes.length} entr{notes.length === 1 ? "y" : "ies"}</span>
                {headerRisk && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.16em] font-extrabold bg-primary/15 text-primary"><AlertTriangle size={11} /> {headerRisk}</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to={`/chat?with=${clientId}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="case-open-chat-btn">
                <MessageSquare size={14} /> Open chat
              </Link>
              <button onClick={exportPdf} disabled={exporting || notes.length === 0} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5 disabled:opacity-50" data-testid="case-export-pdf-btn">
                <Download size={14} /> {exporting ? "Exporting…" : "Export PDF"}
              </button>
              <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="case-add-note-btn">
                <Plus size={14} /> New note
              </button>
            </div>
          </div>

          {/* Mini-dashboard */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="case-mini-dashboard">
            <div className="rounded-2xl bg-primary/5 border border-primary/15 p-4">
              <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-primary mb-1">Completed</div>
              <div className="text-2xl font-extrabold text-[#3D405B]">{completedSessions.length}</div>
              <div className="text-[11px] text-[#7A7D96] mt-0.5">session{completedSessions.length === 1 ? "" : "s"}</div>
            </div>
            <div className="rounded-2xl bg-secondary/10 border border-secondary/20 p-4">
              <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-secondary mb-1 flex items-center gap-1"><Timer size={11} /> Time invested</div>
              <div className="text-2xl font-extrabold text-[#3D405B]">{Math.round(totalMinutes / 60 * 10) / 10}h</div>
              <div className="text-[11px] text-[#7A7D96] mt-0.5">{totalMinutes} min total</div>
            </div>
            <div className="rounded-2xl bg-accent/20 border border-accent/40 p-4">
              <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[#7A5A1E] mb-1 flex items-center gap-1"><CalIcon size={11} /> First seen</div>
              <div className="text-base font-extrabold text-[#3D405B]">{firstSeen ? fmtWhen(firstSeen).split(",")[1]?.trim() || fmtWhen(firstSeen) : "—"}</div>
              <div className="text-[11px] text-[#7A7D96] mt-0.5">on the platform</div>
            </div>
            <div className="rounded-2xl bg-muted/40 border border-[#3D405B]/10 p-4">
              <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[#7A7D96] mb-1">Next session</div>
              <div className="text-base font-extrabold text-[#3D405B]">
                {upcomingSessions[0] ? fmtWhen(upcomingSessions[0].scheduled_at).split(",")[0] : "—"}
              </div>
              <div className="text-[11px] text-[#7A7D96] mt-0.5">
                {upcomingSessions[0] ? fmtWhen(upcomingSessions[0].scheduled_at).split(",").slice(1).join(",").trim() : "no upcoming"}
              </div>
            </div>
          </div>
        </header>

        {/* Sessions timeline */}
        {(upcomingSessions.length + pastSessions.length) > 0 && (
          <section className="brand-card p-6 mb-6" data-testid="case-sessions-timeline">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Sessions</div>
                <h2 className="text-xl font-extrabold text-[#3D405B] tracking-tight">Booking history</h2>
              </div>
              <span className="text-xs text-[#7A7D96]">{upcomingSessions.length} upcoming · {pastSessions.length} past</span>
            </div>
            <div className="space-y-2">
              {[...upcomingSessions.map((b) => ({ ...b, _bucket: "upcoming" })), ...pastSessions.map((b) => ({ ...b, _bucket: "past" }))].map((b) => {
                const pillCls = b._bucket === "upcoming"
                  ? "bg-secondary/15 text-secondary"
                  : b.status === "completed" ? "bg-primary/15 text-primary"
                  : b.status === "cancelled" ? "bg-muted text-[#7A7D96]" : "bg-accent/30 text-[#7A5A1E]";
                return (
                  <Link
                    key={b.booking_id}
                    to={`/session/${b.booking_id}`}
                    className="flex flex-wrap items-center gap-3 p-3 rounded-2xl border border-[#3D405B]/10 hover:bg-[#3D405B]/[.02] transition-colors"
                    data-testid={`case-session-row-${b.booking_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#3D405B] text-sm">{fmtWhen(b.scheduled_at)}</div>
                      <div className="text-xs text-[#7A7D96] mt-0.5 truncate">
                        {b.session_mode === "in_person" ? "In-person" : "Online"} · {b.session_type?.replace("_", " ") || "session"}
                        {b.topic ? ` · ${b.topic}` : ""}
                      </div>
                    </div>
                    <span className={`pill ${pillCls}`}>{b._bucket === "upcoming" ? "upcoming" : b.status}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {showForm && (
          <div className="brand-card p-7 mb-6" data-testid="case-note-form-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">{editing ? "Edit note" : "New note"}</div>
                <h2 className="text-2xl font-extrabold text-[#3D405B] tracking-tight">{editing ? "Update entry" : "Record an interaction"}</h2>
              </div>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-2 rounded-full hover:bg-[#3D405B]/5"><X size={18} /></button>
            </div>
            <NoteForm initial={editing} clientId={clientId} bookings={bookings} onSaved={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }} />
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-[#7A7D96]">Loading case file…</div>
        ) : notes.length === 0 ? (
          <div className="brand-card p-12 text-center">
            <FileText className="mx-auto text-[#7A7D96]/60 mb-3" size={42} />
            <h3 className="font-extrabold text-[#3D405B] text-lg">No entries yet</h3>
            <p className="text-sm text-[#7A7D96] mt-1">Start a clinical record for this client by adding the first note.</p>
            <button onClick={() => setShowForm(true)} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all"><Plus size={14} /> Add first note</button>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((n) => (
              <NoteCard
                key={n.note_id}
                note={n}
                counsellorName={counsellorMap[n.counsellor_id] || (n.counsellor_id === user.user_id ? user.name : "—")}
                canEdit={user.role === "admin" || n.counsellor_id === user.user_id}
                onEdit={() => { setEditing(n); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                onDelete={() => removeNote(n)}
              />
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-[11px] text-[#7A7D96]">
          <ShieldCheck size={11} className="inline mr-1" /> Confidential clinical records — protected under POPIA Section 26.
        </p>
      </main>
    </div>
  );
}
